import { supabase } from '../lib/supabase'
import {
  monthRange,
  toDisplayDate,
  computeCardDueDate,
  effectiveDateFilter,
} from '../lib/dateUtils'
import { projectRecurringRowInRange, type CardCycle } from '../lib/recurringProjection'
import type { Transaction, TransactionFormData } from '../types'

export async function listTransactions(
  householdId: string,
  month: number,
  year: number,
): Promise<Transaction[]> {
  const { from, to } = monthRange(month, year)

  const TX_SELECT = '*, cat:categories!category_id(name), subcat:categories!subcategory_id(name)'

  const [{ data: currentData, error: currentError }, { data: recurringData, error: recurringError }] =
    await Promise.all([
      supabase
        .from('transactions')
        .select(TX_SELECT)
        .eq('household_id', householdId)
        .or(effectiveDateFilter(from, to))
        .order('date', { ascending: false }),
      supabase
        .from('transactions')
        .select(TX_SELECT)
        .eq('household_id', householdId)
        .eq('recurring', true)
        .lt('date', from)
        .order('date', { ascending: false }),
    ])

  if (currentError) throw currentError
  if (recurringError) throw recurringError

  const activeRecurring = recurringData ?? []

  const cardCycles = new Map<string, CardCycle>()
  if (activeRecurring.some(row => (row as { card_id?: string | null }).card_id)) {
    const { data: cardsData, error: cardsError } = await supabase
      .from('cards')
      .select('id, close, due')
      .eq('household_id', householdId)
    if (cardsError) throw cardsError
    for (const c of cardsData ?? [])
      cardCycles.set(c.id, { close: Number(c.close), due: Number(c.due) })
  }

  const projected = activeRecurring.flatMap(row => {
    const cardId = (row as { card_id?: string | null }).card_id
    const cycle = cardId ? cardCycles.get(cardId) : undefined
    return projectRecurringRowInRange(row, from, to, cycle)
  })

  const toTx = (row: typeof projected[0]): Transaction => ({
    id: row.id,
    kind: row.kind as 'in' | 'out',
    title: row.title,
    category: (row as { cat?: { name: string } | null }).cat?.name ?? '',
    category_id: row.category_id as string | null,
    subcategory: (row as { subcat?: { name: string } | null }).subcat?.name ?? '',
    subcategory_id: (row as { subcategory_id?: string | null }).subcategory_id ?? null,
    card_id: (row as { card_id?: string | null }).card_id ?? null,
    note: row.note ?? '',
    date: toDisplayDate(row.date),
    date_iso: row.date,
    created_at: (row as { created_at?: string | null }).created_at ?? row.date,
    due_date: (row as { due_date?: string | null }).due_date ?? null,
    recurring_until: (row as { recurring_until?: string | null }).recurring_until ?? null,
    amount: Number(row.amount),
    recurring: row.recurring,
    installment_group_id: (row as { installment_group_id?: string | null }).installment_group_id ?? null,
    installment_index: (row as { installment_index?: number | null }).installment_index ?? null,
    installment_count: (row as { installment_count?: number | null }).installment_count ?? null,
    realized: (row as { realized?: boolean | null }).realized ?? false,
  })

  return [...(currentData ?? []).map(toTx), ...projected.map(toTx)]
}

function parseAmount(raw: string): number {
  const n = Number(raw.replace(/\./g, '').replace(',', '.'))
  if (!Number.isFinite(n) || n <= 0) throw new Error('Valor deve ser maior que zero')
  return n
}

function addMonths(dateStr: string, months: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const target = new Date(y, m - 1 + months, 1)
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  const day = Math.min(d, lastDay)
  return [
    target.getFullYear().toString(),
    (target.getMonth() + 1).toString().padStart(2, '0'),
    day.toString().padStart(2, '0'),
  ].join('-')
}

// Busca o ciclo (fechamento/vencimento) de um cartão. Lança se não encontrar —
// nunca grava transação de cartão sem due_date silenciosamente (decisão D-D).
async function fetchCardCycle(cardId: string): Promise<{ close: number; due: number }> {
  const { data, error } = await supabase
    .from('cards')
    .select('close, due')
    .eq('id', cardId)
    .single()
  if (error || data?.close == null || data?.due == null)
    throw new Error('Não foi possível carregar os dados do cartão. Tente novamente.')
  return { close: Number(data.close), due: Number(data.due) }
}

export async function createTransaction(
  householdId: string,
  userId: string,
  data: TransactionFormData,
): Promise<void> {
  const title = data.title.trim()
  if (title.length === 0 || title.length > 100)
    throw new Error('Descrição deve ter entre 1 e 100 caracteres')
  if (data.kind !== 'in' && data.kind !== 'out')
    throw new Error('Tipo de lançamento inválido')
  if (!data.date)
    throw new Error('Data de cadastro é obrigatória')

  const amount = parseAmount(data.amount)

  if (data.installment_count > 1) {
    const n = data.installment_count
    const groupId = crypto.randomUUID()
    const total = amount

    let perInstallment: number
    let lastInstallment: number
    if (data.installment_mode === 'total') {
      perInstallment = Math.floor(total * 100 / n) / 100
      lastInstallment = Math.round((total - perInstallment * (n - 1)) * 100) / 100
    } else {
      perInstallment = total
      lastInstallment = total
    }

    // due_date da 1ª parcela; demais parcelas somam 1 mês exato à fatura anterior
    // (C6: evita colapso de parcelas em meses curtos como fim de fevereiro).
    let firstDueDate: string | null = null
    if (data.card_id) {
      const { close, due } = await fetchCardCycle(data.card_id)
      firstDueDate = computeCardDueDate(data.date, close, due)
    }

    const rows = Array.from({ length: n }, (_, i) => ({
      household_id: householdId,
      created_by: userId,
      kind: data.kind,
      amount: i === n - 1 ? lastInstallment : perInstallment,
      title: `${title} (${i + 1}/${n})`,
      category_id: data.category_id || null,
      subcategory_id: data.subcategory_id || null,
      card_id: data.card_id || null,
      note: 'parcelado',
      date: addMonths(data.date, i),
      due_date: firstDueDate ? addMonths(firstDueDate, i) : null,
      recurring: false,
      recurring_until: null,
      realized: data.realized,
      installment_group_id: groupId,
      installment_index: i + 1,
      installment_count: n,
    }))
    const { error } = await supabase.from('transactions').insert(rows)
    if (error) throw error
    return
  }

  // Transação avulsa: se for de cartão, due_date é calculado pelo ciclo da fatura;
  // caso contrário usa o vencimento manual informado no formulário.
  let computedDueDate: string | null = data.due_date || null
  if (data.card_id) {
    const { close, due } = await fetchCardCycle(data.card_id)
    computedDueDate = computeCardDueDate(data.date, close, due)
  }

  const { error } = await supabase.from('transactions').insert({
    household_id: householdId,
    created_by: userId,
    kind: data.kind,
    amount,
    title,
    category_id: data.category_id || null,
    subcategory_id: data.subcategory_id || null,
    card_id: data.card_id || null,
    note: data.recurring ? 'recorrente' : 'avulso',
    date: data.date,
    due_date: computedDueDate,
    recurring: data.recurring,
    recurring_until: data.recurring && data.recurring_until ? data.recurring_until : null,
    realized: data.realized,
  })

  if (error) throw error
}

export async function updateTransaction(id: string, data: TransactionFormData): Promise<void> {
  const title = data.title.trim()
  if (title.length === 0 || title.length > 100)
    throw new Error('Descrição deve ter entre 1 e 100 caracteres')
  if (data.kind !== 'in' && data.kind !== 'out')
    throw new Error('Tipo de lançamento inválido')

  const amount = parseAmount(data.amount)

  const newCardId = data.card_id || null
  let computedDueDate: string | null
  if (!newCardId) {
    // Sem cartão: due_date vem do vencimento manual. Se a transação tinha cartão
    // e o cartão foi removido, isto zera o due_date herdado (C4).
    computedDueDate = data.due_date || null
  } else {
    // Sempre recalcula com o ciclo atual do cartão, mesmo que só título/categoria/valor
    // tenham mudado. Isso garante que se o usuário alterou close/due do cartão, a próxima
    // edição da transação corrige o due_date sem precisar de backfill manual.
    // Reverte C7: o comportamento anterior preservava o due_date gravado quando apenas
    // metadados mudavam, mas impedia a correção após mudança do ciclo do cartão.
    const { close, due } = await fetchCardCycle(newCardId)
    computedDueDate = computeCardDueDate(data.date, close, due)
  }

  const { error } = await supabase
    .from('transactions')
    .update({
      kind: data.kind,
      amount,
      title,
      category_id: data.category_id || null,
      subcategory_id: data.subcategory_id || null,
      card_id: newCardId,
      note: data.recurring ? 'recorrente' : 'avulso',
      date: data.date,
      due_date: computedDueDate,
      recurring: data.recurring,
      recurring_until: data.recurring && data.recurring_until ? data.recurring_until : null,
      realized: data.realized,
    })
    .eq('id', id)

  if (error) throw error
}

export async function deleteTransaction(id: string): Promise<void> {
  const { error } = await supabase.from('transactions').delete().eq('id', id)
  if (error) throw error
}

export async function updateInstallmentGroup(
  householdId: string,
  userId: string,
  groupId: string,
  currentDate: string,
  currentIndex: number,
  data: TransactionFormData,
): Promise<void> {
  const title = data.title.trim()
  const n = data.installment_count
  const amount = parseAmount(data.amount)
  const baseDate = addMonths(currentDate, -(currentIndex - 1))

  let firstDueDate: string | null = null
  if (data.card_id) {
    const { close, due } = await fetchCardCycle(data.card_id)
    firstDueDate = computeCardDueDate(baseDate, close, due)
  }

  let perInstallment: number
  let lastInstallment: number
  if (data.installment_mode === 'total') {
    perInstallment = Math.floor(amount * 100 / n) / 100
    lastInstallment = Math.round((amount - perInstallment * (n - 1)) * 100) / 100
  } else {
    perInstallment = amount
    lastInstallment = amount
  }

  const { data: existing, error: fetchErr } = await supabase
    .from('transactions')
    .select('id, installment_index, realized')
    .eq('installment_group_id', groupId)
    .order('installment_index')
  if (fetchErr) throw fetchErr

  const existingCount = existing?.length ?? 0
  const toUpdate = (existing ?? []).slice(0, Math.min(n, existingCount))

  for (let i = 0; i < toUpdate.length; i++) {
    const { error } = await supabase
      .from('transactions')
      .update({
        kind: data.kind,
        title: `${title} (${i + 1}/${n})`,
        amount: i === n - 1 ? lastInstallment : perInstallment,
        category_id: data.category_id || null,
        subcategory_id: data.subcategory_id || null,
        card_id: data.card_id || null,
        date: addMonths(baseDate, i),
        due_date: firstDueDate ? addMonths(firstDueDate, i) : null,
        realized: toUpdate[i].installment_index === currentIndex ? data.realized : toUpdate[i].realized,
        installment_count: n,
        installment_index: i + 1,
      })
      .eq('id', toUpdate[i].id)
    if (error) throw error
  }

  if (n < existingCount) {
    const idsToDelete = (existing ?? []).slice(n).map(r => r.id)
    const { error } = await supabase.from('transactions').delete().in('id', idsToDelete)
    if (error) throw error
  }

  if (n > existingCount) {
    const newRows = Array.from({ length: n - existingCount }, (_, i) => {
      const idx = existingCount + i
      return {
        household_id: householdId,
        created_by: userId,
        kind: data.kind,
        amount: idx === n - 1 ? lastInstallment : perInstallment,
        title: `${title} (${idx + 1}/${n})`,
        category_id: data.category_id || null,
        subcategory_id: data.subcategory_id || null,
        card_id: data.card_id || null,
        note: 'parcelado',
        date: addMonths(baseDate, idx),
        due_date: firstDueDate ? addMonths(firstDueDate, idx) : null,
        recurring: false,
        recurring_until: null,
        realized: false,
        installment_group_id: groupId,
        installment_index: idx + 1,
        installment_count: n,
      }
    })
    const { error } = await supabase.from('transactions').insert(newRows)
    if (error) throw error
  }
}

export async function deleteInstallmentGroup(groupId: string): Promise<void> {
  const { error } = await supabase.from('transactions').delete().eq('installment_group_id', groupId)
  if (error) throw error
}

export async function amortizeInstallments(
  groupId: string,
  currentIndex: number,
  targetDate: string,
  count: number,
): Promise<void> {
  const { data, error: fetchError } = await supabase
    .from('transactions')
    .select('id, card_id')
    .eq('installment_group_id', groupId)
    .gt('installment_index', currentIndex)
    .order('installment_index', { ascending: false })
    .limit(count)
  if (fetchError) throw fetchError
  if (!data || data.length === 0) throw new Error('Nenhuma parcela futura encontrada')
  const ids = data.map((r: { id: string }) => r.id)

  // Todas as parcelas antecipadas passam para a mesma targetDate, logo compartilham
  // o mesmo due_date recalculado (quando o grupo é de cartão).
  const cardId = data.find((r: { card_id?: string | null }) => r.card_id)?.card_id ?? null
  const payload: { date: string; due_date?: string | null } = { date: targetDate }
  if (cardId) {
    const { close, due } = await fetchCardCycle(cardId)
    payload.due_date = computeCardDueDate(targetDate, close, due)
  }

  const { error: updateError } = await supabase
    .from('transactions')
    .update(payload)
    .in('id', ids)
  if (updateError) throw updateError
}

export async function deleteAllTransactions(householdId: string): Promise<void> {
  const { error } = await supabase.from('transactions').delete().eq('household_id', householdId)
  if (error) throw error
}

export async function countAllTransactions(householdId: string): Promise<number> {
  const { count, error } = await supabase
    .from('transactions')
    .select('*', { count: 'exact', head: true })
    .eq('household_id', householdId)
  if (error) throw error
  return count ?? 0
}

export async function toggleRealized(id: string, realized: boolean): Promise<void> {
  const { error } = await supabase
    .from('transactions')
    .update({ realized })
    .eq('id', id)
  if (error) throw error
}

export async function markInvoiceAsPaid(ids: string[]): Promise<void> {
  if (ids.length === 0) return
  const { error } = await supabase
    .from('transactions')
    .update({ realized: true })
    .in('id', ids)
  if (error) throw error
}
